import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { DemNotificationsSlot } from '../dem/DemNotificationsSlot';
import { GlobalSearch } from '../search/GlobalSearch';
import { useAuth } from '../../contexts/AuthContext';
import { usePortal } from '../../contexts/PortalContext';
import { useMaxWidth } from '../../hooks/useMaxWidth';
import { DEM_DEFAULT_SLUG, DEM_MAIN_MODULES } from '../../lib/demModules';
import { formatDisplayPersonName } from '../../lib/formatPersonName';
import { PortalSwitcher } from './PortalSwitcher';
import '../../styles/dem-theme-lab.css';

const PORTAL_NAME = 'Data Entry Management';

type DemFloatingShellProps = {
  children: ReactNode;
  /** Optional page title override (defaults from current DEM module). */
  pageTitle?: string;
  /** Hide the in-main title/search row (page owns its header). */
  hideMainTop?: boolean;
};

export function DemFloatingShell({
  children,
  pageTitle,
  hideMainTop = false,
}: DemFloatingShellProps) {
  const { profile, signOut, role: userRole } = useAuth();
  const { basePath } = usePortal();
  const location = useLocation();
  const isMobile = useMaxWidth(960);
  const [collapsed, setCollapsed] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const railRef = useRef<HTMLDivElement>(null);
  const railScaleRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const flipTimerRef = useRef<number | null>(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountBtnRef = useRef<HTMLButtonElement>(null);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const [accountMenuPos, setAccountMenuPos] = useState<{
    top?: number;
    bottom?: number;
    left: number;
  }>({ left: 0 });
  const [flyTip, setFlyTip] = useState<{ label: string; top: number; left: number } | null>(
    null,
  );

  const displayName =
    formatDisplayPersonName(profile?.display_name?.trim() || profile?.username) || 'User';
  const initials =
    displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('') || 'U';
  const roleLabel = (profile?.role ?? userRole ?? 'user').toUpperCase();

  const navItems = useMemo(
    () =>
      DEM_MAIN_MODULES.map((mod) => {
        const to = basePath ? `${basePath}/${mod.slug}` : `/${mod.slug}`;
        const home = basePath ? `${basePath}/${DEM_DEFAULT_SLUG}` : `/${DEM_DEFAULT_SLUG}`;
        return {
          to,
          label: mod.label,
          icon: mod.icon,
          end: to === home,
        };
      }),
    [basePath],
  );

  const resolvedTitle = useMemo(() => {
    if (pageTitle) return pageTitle;
    if (/\/theme-preview\/?$/.test(location.pathname)) return 'Theme preview';
    if (/\/deliveries\/new\/?$/.test(location.pathname)) return 'New delivery';
    if (/\/deliveries\/[^/]+\/?$/.test(location.pathname)) return 'Delivery';
    if (/\/blanket-orders\/new\/?$/.test(location.pathname)) return 'New blanket';
    if (/\/blanket-orders\/[^/]+\/?$/.test(location.pathname)) return 'Blanket order';
    if (/\/customer-feedback\/new\/?$/.test(location.pathname)) return 'New CFF';
    if (/\/customer-feedback\/[^/]+\/?$/.test(location.pathname)) return 'CFF';
    if (/\/customer-feedback\/?$/.test(location.pathname)) return 'CFF';
    if (/\/pricing-update\/new\/?$/.test(location.pathname)) return 'New price update';
    if (/\/pricing-update\/[^/]+\/?$/.test(location.pathname)) return 'Price update';
    if (/\/pricing-update\/?$/.test(location.pathname)) return 'Price update';
    if (/\/sku-map\/?$/.test(location.pathname)) return 'SKU Map';
    if (/\/items\/?$/.test(location.pathname)) return 'Items';
    if (/\/customers\/?$/.test(location.pathname)) return 'Customers';
    if (/\/standard-sales-lines\/?$/.test(location.pathname)) return 'Std Lines';
    if (/\/cloudtalk\/?$/.test(location.pathname)) return 'CloudTalk';
    const match = DEM_MAIN_MODULES.find((mod) => {
      const to = basePath ? `${basePath}/${mod.slug}` : `/${mod.slug}`;
      if (mod.slug === DEM_DEFAULT_SLUG) {
        return location.pathname === to || location.pathname === `${to}/`;
      }
      return location.pathname === to || location.pathname.startsWith(`${to}/`);
    });
    return match?.title ?? match?.label ?? 'Dashboard';
  }, [pageTitle, basePath, location.pathname]);

  useLayoutEffect(() => {
    if (!accountOpen || !accountBtnRef.current) return;
    const update = () => {
      const rect = accountBtnRef.current!.getBoundingClientRect();
      const menuEl = accountMenuRef.current;
      const menuWidth = menuEl?.offsetWidth || (isMobile ? 296 : 232);
      const menuHeight = menuEl?.offsetHeight || 200;

      if (isMobile) {
        const width = Math.min(menuWidth, window.innerWidth - 24);
        // Anchor by bottom so expanding Choose portal grows upward (no top jump/flash).
        let left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
        const bottom = Math.max(12, window.innerHeight - rect.top + 10);
        setAccountMenuPos({ bottom, left });
        return;
      }

      let left = rect.right + 10;
      if (left + menuWidth > window.innerWidth - 12) {
        left = Math.max(12, rect.left - menuWidth - 10);
      }
      let top = rect.bottom - menuHeight;
      if (top < 12) top = 12;
      if (top + menuHeight > window.innerHeight - 12) {
        top = Math.max(12, window.innerHeight - menuHeight - 12);
      }
      setAccountMenuPos({ top, left });
    };
    update();
    const raf = requestAnimationFrame(update);
    const menuEl = accountMenuRef.current;
    const ro =
      !isMobile && typeof ResizeObserver !== 'undefined' && menuEl
        ? new ResizeObserver(() => update())
        : null;
    if (menuEl && ro) ro.observe(menuEl);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [accountOpen, collapsed, isMobile]);

  useEffect(() => {
    if (!accountOpen) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (accountBtnRef.current?.contains(target)) return;
      if (accountMenuRef.current?.contains(target)) return;
      setAccountOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [accountOpen]);

  useEffect(() => {
    return () => {
      if (flipTimerRef.current != null) window.clearTimeout(flipTimerRef.current);
    };
  }, []);

  useEffect(() => {
    setMobileNavOpen(false);
    setAccountOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobile) {
      setMobileNavOpen(false);
      return;
    }
    setMobileNavOpen(false);
    setCollapsed(false);
  }, [isMobile]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  const toggleCollapsed = () => {
    setAccountOpen(false);
    setFlyTip(null);

    // Mobile uses the menu drawer — no rail FLIP animation.
    if (isMobile) {
      setMobileNavOpen((v) => !v);
      return;
    }

    const rail = railRef.current;
    const railScale = railScaleRef.current;
    const main = mainRef.current;
    const ease = 'cubic-bezier(0.33, 1, 0.32, 1)';
    const dur = 520;

    // Fallback if refs missing or already animating.
    if (!rail || !railScale || !main || animating) {
      setCollapsed((v) => !v);
      return;
    }

    const railFirst = rail.getBoundingClientRect();
    const mainFirst = main.getBoundingClientRect();
    setAnimating(true);
    setCollapsed((v) => !v);

    requestAnimationFrame(() => {
      const railLast = rail.getBoundingClientRect();
      const mainLast = main.getBoundingClientRect();
      const railSx = railFirst.width / Math.max(railLast.width, 1);
      const mainSx = mainFirst.width / Math.max(mainLast.width, 1);
      const mainDx = mainFirst.left - mainLast.left;

      rail.style.transformOrigin = 'left center';
      rail.style.transition = 'none';
      rail.style.transform = `scaleX(${railSx})`;
      railScale.style.transformOrigin = 'left center';
      railScale.style.transition = 'none';
      railScale.style.transform = `scaleX(${1 / railSx})`;

      main.style.transformOrigin = 'left top';
      main.style.transition = 'none';
      main.style.transform = `translateX(${mainDx}px) scaleX(${mainSx})`;

      requestAnimationFrame(() => {
        rail.style.transition = `transform ${dur}ms ${ease}`;
        railScale.style.transition = `transform ${dur}ms ${ease}`;
        main.style.transition = `transform ${dur}ms ${ease}`;
        rail.style.transform = 'scaleX(1)';
        railScale.style.transform = 'scaleX(1)';
        main.style.transform = 'translateX(0) scaleX(1)';

        const clear = () => {
          if (flipTimerRef.current != null) {
            window.clearTimeout(flipTimerRef.current);
            flipTimerRef.current = null;
          }
          rail.style.transition = '';
          rail.style.transform = '';
          railScale.style.transition = '';
          railScale.style.transform = '';
          main.style.transition = '';
          main.style.transform = '';
          setAnimating(false);
          window.dispatchEvent(new Event('resize'));
        };

        flipTimerRef.current = window.setTimeout(clear, dur + 50);
        const onEnd = (e: TransitionEvent) => {
          if (e.propertyName !== 'transform') return;
          main.removeEventListener('transitionend', onEnd);
          clear();
        };
        main.addEventListener('transitionend', onEnd);
      });
    });
  };

  const showNavTip = (label: string, el: HTMLElement) => {
    if (!collapsed) return;
    const rect = el.getBoundingClientRect();
    setFlyTip({
      label,
      top: rect.top + rect.height / 2,
      left: rect.right + 10,
    });
  };

  const accountMenu = accountOpen
    ? createPortal(
        <div
          ref={accountMenuRef}
          className="dem-theme-lab__account-menu"
          role="menu"
          style={
            accountMenuPos.bottom != null
              ? { top: 'auto', bottom: accountMenuPos.bottom, left: accountMenuPos.left }
              : { top: accountMenuPos.top, left: accountMenuPos.left }
          }
        >
          <div className="dem-theme-lab__account-menu-head">
            <strong>{displayName}</strong>
            <span>{roleLabel}</span>
          </div>
          <div className="dem-theme-lab__account-menu-portal">
            <PortalSwitcher current="dem" triggerLabel="Choose portal" hideIcon />
          </div>
          <button
            type="button"
            role="menuitem"
            className="dem-theme-lab__account-signout"
            onClick={() => {
              setAccountOpen(false);
              void signOut();
            }}
          >
            Sign out
          </button>
        </div>,
        document.body,
      )
    : null;

  return (
    <div
      className={`dem-theme-lab${collapsed ? ' is-collapsed' : ''}${
        animating ? ' is-animating' : ''
      }${mobileNavOpen ? ' is-mobile-nav-open' : ''}`}
    >
      <div className="dem-theme-lab__rail" ref={railRef}>
        <div className="dem-theme-lab__rail-scale" ref={railScaleRef}>
          <div className="dem-theme-lab__logo-pod" aria-label="BTH">
            <img className="dem-theme-lab__bth-logo" src="/bth-logo.png" alt="Ban Teck Han" />
            <img className="dem-theme-lab__app-icon" src="/app-icon.png" alt="" aria-hidden />
          </div>

          <div className="dem-theme-lab__mobile-chrome">
            <div className="dem-theme-lab__notify dem-theme-lab__notify--mobile-bar">
              <DemNotificationsSlot placement="below" />
            </div>
            <button
              type="button"
              className="dem-theme-lab__mobile-menu-btn"
              aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileNavOpen}
              aria-controls="dem-theme-lab-sidebar"
              onClick={() => {
                setAccountOpen(false);
                setMobileNavOpen((v) => !v);
              }}
            >
              <span aria-hidden className="dem-theme-lab__mobile-menu-icon">
                {mobileNavOpen ? '✕' : '☰'}
              </span>
            </button>
          </div>

          {mobileNavOpen ? (
            <button
              type="button"
              className="dem-theme-lab__mobile-scrim"
              aria-label="Close menu"
              onClick={() => setMobileNavOpen(false)}
            />
          ) : null}

          <aside
            id="dem-theme-lab-sidebar"
            className="dem-theme-lab__sidebar"
            aria-label="DEM navigation"
            aria-hidden={isMobile ? !mobileNavOpen : undefined}
          >
            <div className="dem-theme-lab__sidebar-top">
              <div className="dem-theme-lab__page-title" title={PORTAL_NAME}>
                {PORTAL_NAME}
              </div>
              <button
                type="button"
                className="dem-theme-lab__collapse"
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                title={collapsed ? 'Expand' : 'Collapse'}
                onClick={toggleCollapsed}
              >
                <span aria-hidden className="dem-theme-lab__collapse-chevron">
                  {collapsed ? '›' : '‹'}
                </span>
              </button>
              <button
                type="button"
                className="dem-theme-lab__mobile-drawer-close"
                aria-label="Close menu"
                onClick={() => setMobileNavOpen(false)}
              >
                <span aria-hidden>✕</span>
              </button>
            </div>

            <nav className="dem-theme-lab__nav">
              {navItems.map((item, index) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `dem-theme-lab__nav-item${isActive ? ' is-active' : ''}`
                  }
                  style={{ animationDelay: `${index * 28}ms` }}
                  onMouseEnter={(e) => showNavTip(item.label, e.currentTarget)}
                  onMouseLeave={() => setFlyTip(null)}
                  onFocus={(e) => showNavTip(item.label, e.currentTarget)}
                  onBlur={() => setFlyTip(null)}
                  onClick={() => setMobileNavOpen(false)}
                >
                  <span className="dem-theme-lab__nav-icon" aria-hidden>
                    {item.icon}
                  </span>
                  <span className="dem-theme-lab__nav-label">{item.label}</span>
                </NavLink>
              ))}
            </nav>

            <div className="dem-theme-lab__sidebar-foot">
              <div className="dem-theme-lab__foot-actions">
                <div className="dem-theme-lab__account">
                  <button
                    ref={accountBtnRef}
                    type="button"
                    className="dem-theme-lab__account-btn"
                    aria-expanded={accountOpen}
                    aria-haspopup="menu"
                    title={displayName}
                    onClick={() => setAccountOpen((v) => !v)}
                  >
                    <span className="dem-theme-lab__avatar" aria-hidden>
                      {initials}
                    </span>
                    <span className="dem-theme-lab__user">
                      <strong>{displayName}</strong>
                      <span>{roleLabel}</span>
                    </span>
                  </button>
                </div>

                <div className="dem-theme-lab__notify dem-theme-lab__notify--rail">
                  <DemNotificationsSlot placement="above" />
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {accountMenu}

      {flyTip && collapsed
        ? createPortal(
            <div
              className="dem-theme-lab__fly-tip"
              style={{ top: flyTip.top, left: flyTip.left }}
              role="tooltip"
            >
              {flyTip.label}
            </div>,
            document.body,
          )
        : null}

      <div className="dem-theme-lab__main" ref={mainRef}>
        {!hideMainTop ? (
          <div className="dem-theme-lab__top">
            <div className="dem-theme-lab__title">
              <h1>{resolvedTitle}</h1>
              <p>Welcome back, {displayName}</p>
            </div>
            <div className="dem-theme-lab__search-wrap">
              <GlobalSearch />
            </div>
          </div>
        ) : null}
        <div className="dem-theme-lab__shell">{children}</div>
      </div>
    </div>
  );
}
